import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, Package, ShoppingCart, Users, BarChart3, Plus, Edit, Trash2, Eye, Search, Mail, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel = ({ navigateHome }) => {
  const { user, isAuthenticated, getIdToken, testLogin } = useAuth();
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  
  // Marketplace stats
  const [marketplaceStats, setMarketplaceStats] = useState({
    applications: 0,
    activeListings: 0,
    totalSales: 0
  });

  // Marketplace data
  const [pendingListings, setPendingListings] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);

  // Admin marketplace management state
  const [allListings, setAllListings] = useState([]);
  const [allDesigners, setAllDesigners] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [showListingEditor, setShowListingEditor] = useState(false);
  const [showDesignerEditor, setShowDesignerEditor] = useState(false);
  const [marketplaceView, setMarketplaceView] = useState('overview'); // overview, listings, designers

  
  // Contact submissions
  const [contactSubmissions, setContactSubmissions] = useState([]);

  // Inventory management state
  const [inventory, setInventory] = useState([]);
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [selectedProductForInventory, setSelectedProductForInventory] = useState(null);
  const [inventoryAdjustment, setInventoryAdjustment] = useState({
    quantity_change: '',
    reason: '',
    notes: ''
  });

  // Reviews management state
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('all');

  // CMS Editor state
  const [cmsPages, setCmsPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showCmsEditor, setShowCmsEditor] = useState(false);
  const [cmsForm, setCmsForm] = useState({
    title: '',
    content: '',
    meta_description: '',
    is_published: true
  });


  // User Management state
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserEditor, setShowUserEditor] = useState(false);

  // Newsletter Management state
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterSearchTerm, setNewsletterSearchTerm] = useState('');


  // Order Update state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderUpdateModal, setShowOrderUpdateModal] = useState(false);
  const [orderUpdateForm, setOrderUpdateForm] = useState({
    status: '',
    tracking_number: '',
    tracking_carrier: '',
    note: '',
    internal_note: '',
    notify_customer: true
  });
  const [orderHistory, setOrderHistory] = useState([]);


  // Product form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    manufacturer: '',
    product_type: 'FDM Printer',
    category: '',
    price: '',
    image_urls: [], // Changed from image_url to image_urls array
    stock_quantity: '',
    sku: '',
    specifications: {
      build_volume: '',
      layer_height: '',
      print_speed: '',
      connectivity: '',
      material_compatibility: '',
      diameter: '',
      color: '',
      weight: ''
    },
    tags: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      checkAdminStatus();
    }
  }, [isAuthenticated]);

  const checkAdminStatus = async () => {
    try {
      // Test admin bypass for development
      if (user?.email === 'test@admin.com') {
        console.log('Test admin detected - granting admin access');
        setIsAdmin(true);
        loadProducts();
        loadOrders();
        loadMarketplaceStats();
        loadInventory();
        loadReviews();
        loadCmsPages();
        loadNewsletterSubscribers();
        setCheckingAdmin(false);
        return;
      }
      
      // Check if user is admin by trying to access an admin endpoint
      const response = await makeAuthenticatedRequest('/api/orders/admin/all');
      if (response.ok) {
        setIsAdmin(true);
        loadProducts();
        loadOrders();
        loadMarketplaceStats(); // Load marketplace data
        loadInventory();
        loadReviews();
        loadCmsPages();
        loadNewsletterSubscribers();
      } else if (response.status === 403) {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  const loadMarketplaceStats = async () => {
    try {
      // Load designer applications
      const appsResponse = await makeAuthenticatedRequest('/api/marketplace/applications');
      const applications = appsResponse.ok ? (await appsResponse.json()).applications || [] : [];
      
      // Filter for PENDING applications only (those needing admin review) 
      const pendingApplications = applications.filter(app => app.status === 'pending');
      
      // Get recent applications for display (all statuses for admin visibility)
      const sortedApplications = applications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      // Load ALL marketplace listings for admin (including drafts)
      // Since /api/marketplace/listings only returns active listings, we need admin access
      const listingsResponse = await makeAuthenticatedRequest('/api/admin/marketplace/listings');
      const listings = listingsResponse.ok ? (await listingsResponse.json()).listings || [] : [];
      
      // Filter for draft and active listings (draft = pending approval)
      const pendingListingsData = listings.filter(l => l.status === 'draft');
      const activeListings = listings.filter(l => l.status === 'active').length;
      const totalSales = listings.reduce((sum, l) => sum + (l.total_earnings || 0), 0);
      
      // Update states
      setMarketplaceStats({
        applications: pendingApplications.length, // Only pending applications
        activeListings: activeListings,
        totalSales: totalSales
      });
      
      // Set pending listings and recent applications for display
      setPendingListings(pendingListingsData);
      setRecentApplications(sortedApplications.slice(0, 5)); // Show last 5 applications (any status)
      
    } catch (error) {
      console.error('Error loading marketplace stats:', error);
    }
  };

  const loadNewsletterSubscribers = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/newsletter/subscribers');
      if (response.ok) {
        const data = await response.json();
        setNewsletterSubscribers(data.subscribers || []);
      }
    } catch (error) {
      console.error('Error loading newsletter subscribers:', error);
    }
  };

  const removeNewsletterSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from newsletter?`)) return;
    
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/newsletter/subscribers/${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        alert('Subscriber removed successfully');
        loadNewsletterSubscribers();
      } else {
        alert('Failed to remove subscriber');
      }
    } catch (error) {
      console.error('Error removing subscriber:', error);
      alert('Error removing subscriber');
    }
  };


  // ========== ADMIN MARKETPLACE/DESIGNER MANAGEMENT ==========
  const loadAllListings = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/marketplace/listings');
      if (response.ok) {
        const data = await response.json();
        setAllListings(data.listings || []);
      }
    } catch (error) {
      console.error('Error loading all listings:', error);
    }
  };

  const loadAllDesigners = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/designers');
      if (response.ok) {
        const data = await response.json();
        setAllDesigners(data.designers || []);
      }
    } catch (error) {
      console.error('Error loading all designers:', error);
    }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) return;
    
    const reason = prompt('Enter deletion reason (optional):');
    // If user cancels the prompt, reason will be null, so abort
    if (reason === null) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/marketplace/listings/${listingId}?reason=${encodeURIComponent(reason || '')}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        alert('Listing deleted successfully!');
        loadAllListings();
      } else {
        alert('Error deleting listing: ' + response.statusText);
      }
    } catch (error) {
      console.error('Delete listing error:', error);
      alert('Error deleting listing: ' + error.message);
    }
  };

  const suspendDesigner = async (designerId) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/designers/${designerId}/suspend`,
        'POST',
        { reason }
      );
      if (response.ok) {
        alert('Designer suspended successfully!');
        loadAllDesigners();
      }
    } catch (error) {
      alert('Error suspending designer');
    }
  };

  const unsuspendDesigner = async (designerId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/designers/${designerId}/unsuspend`,
        'POST'
      );
      if (response.ok) {
        alert('Designer unsuspended successfully!');
        loadAllDesigners();
      }
    } catch (error) {
      alert('Error unsuspending designer');
    }
  };

  const removeDesignerStatus = async (designerId) => {
    const reason = prompt('Enter reason for removing designer status:');
    if (!reason) return;
    if (!window.confirm('This will permanently remove designer privileges. Continue?')) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/designers/${designerId}/remove`,
        'DELETE',
        { reason }
      );
      if (response.ok) {
        alert('Designer status removed successfully!');
        loadAllDesigners();
      }
    } catch (error) {
      alert('Error removing designer status');
    }
  };


  // ========== USER MANAGEMENT FUNCTIONS ==========
  const loadAllUsers = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/users');
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const banUser = async (userId) => {
    const reason = prompt('Enter ban reason:');
    if (!reason) return;
    if (!window.confirm('Are you sure you want to ban this user?')) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/users/${userId}/ban`,
        {
          method: 'POST',
          body: JSON.stringify({ reason })
        }
      );
      if (response.ok) {
        alert('User banned successfully!');
        loadAllUsers();
      }
    } catch (error) {
      alert('Error banning user');
    }
  };

  const unbanUser = async (userId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/admin/users/${userId}/unban`, { method: 'POST' });
      if (response.ok) {
        alert('User unbanned successfully!');
        loadAllUsers();
      }
    } catch (error) {
      alert('Error unbanning user');
    }
  };

  // ========== FILE DOWNLOAD FUNCTIONS ==========
  const [quotes, setQuotes] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);

  const loadQuotes = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/quotes/all');
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Error loading quotes:', error);
    }
  };

  const loadServiceRequests = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/service-requests/all');
      if (response.ok) {
        const data = await response.json();
        setServiceRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error loading service requests:', error);
    }
  };

  const downloadQuoteFile = async (quoteId) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/download/quote/${quoteId}`,
        {
          headers: { 'Authorization': `Bearer ${idToken}` }
        }
      );
      
      if (response.ok) {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `quote_${quoteId}.file`; // Default fallback
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error downloading file');
      }
    } catch (error) {
      alert('Error downloading file');
    }
  };

  const downloadServiceFile = async (requestId) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/download/service/${requestId}`,
        {
          headers: { 'Authorization': `Bearer ${idToken}` }
        }
      );
      
      if (response.ok) {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `service_${requestId}.file`; // Default fallback
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error downloading file');
      }
    } catch (error) {
      alert('Error downloading file');
    }
  };

  // ========== ORDER UPDATE FUNCTIONS ==========
  const openOrderUpdate = (order) => {
    setSelectedOrder(order);
    setOrderUpdateForm({
      status: order.status || '',
      tracking_number: order.tracking_number || '',
      tracking_carrier: order.tracking_carrier || '',
      note: '',
      internal_note: '',
      notify_customer: true
    });
    loadOrderHistory(order.id);
    setShowOrderUpdateModal(true);
  };

  const loadOrderHistory = async (orderId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/admin/orders/${orderId}/history`);
      if (response.ok) {
        const data = await response.json();
        setOrderHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error loading order history:', error);
    }
  };

  const submitOrderUpdate = async () => {
    if (!selectedOrder) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/orders/${selectedOrder.id}/update`,
        {
          method: 'POST',
          body: JSON.stringify(orderUpdateForm)
        }
      );

      if (response.ok) {
        alert('Order updated successfully!');
        setShowOrderUpdateModal(false);
        setSelectedOrder(null);
        loadOrders();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'Failed to update order'));
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Error updating order');
    }
  };

  const addOrderNote = async (note, isInternal = false) => {
    if (!selectedOrder || !note.trim()) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/orders/${selectedOrder.id}/note`,
        {
          method: 'POST',
          body: JSON.stringify({ note, is_internal: isInternal })
        }
      );

      if (response.ok) {
        alert('Note added successfully!');
        loadOrderHistory(selectedOrder.id);
      } else {
        alert('Error adding note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Error adding note');
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/admin/marketplace/listings/${listingId}/approve`, {
        method: 'POST'
      });

      if (response.ok) {
        alert('Listing approved successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to approve listing: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving listing:', error);
      alert('Failed to approve listing. Please try again.');
    }
  };

  const handleRejectListing = async (listingId) => {
    const reason = prompt('Please provide a reason for rejecting this listing (optional):');
    
    try {
      const url = `/api/admin/marketplace/listings/${listingId}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`;
      const response = await makeAuthenticatedRequest(url, {
        method: 'POST'
      });

      if (response.ok) {
        alert('Listing rejected successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to reject listing: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting listing:', error);
      alert('Failed to reject listing. Please try again.');
    }
  };

  const handleApproveApplication = async (applicationId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/marketplace/applications/${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'approved'
        })
      });

      if (response.ok) {
        alert('Designer application approved successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to approve application: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving application:', error);
      alert('Failed to approve application. Please try again.');
    }
  };

  const handleRejectApplication = async (applicationId) => {
    const notes = prompt('Please provide a reason for rejecting this application (optional):');
    
    try {
      const body = { status: 'rejected' };
      if (notes) {
        body.admin_notes = notes;
      }

      const response = await makeAuthenticatedRequest(`/api/marketplace/applications/${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        alert('Designer application rejected successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to reject application: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      alert('Failed to reject application. Please try again.');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadProducts();
      loadOrders();
      loadContactSubmissions();
    }
  }, [isAuthenticated]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Authentication required');
    }

    // Use environment backend URL for all users (including test admin)
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let apiUrl = backendUrl;
    if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
      apiUrl = '';
    }

    return fetch(`${apiUrl}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        ...options.headers
      },
      credentials: 'include'
    });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const idToken = await getIdToken();
      if (!idToken) {
        alert('Authentication required');
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/admin/upload-product-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        // Add new image URLs to the existing array with correct API prefix
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        let baseUrl = backendUrl;
        if (baseUrl.includes('None') || !baseUrl || baseUrl === '') {
          // For local development, use relative URLs
          baseUrl = '';
        }
        
        const newImageUrls = result.images.map(img => `${baseUrl}${img.url}`);
        
        setProductForm(prev => ({
          ...prev,
          image_urls: [...(prev.image_urls || []), ...newImageUrls]
        }));
        
        console.log('Uploaded image URLs:', newImageUrls); // Debug logging
        alert(`${result.image_count} image(s) uploaded successfully!`);
      } else {
        throw new Error(result.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(`Failed to upload images: ${error.message}`);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/products?per_page=100`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/orders/admin/all');
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };


  const loadContactSubmissions = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/contact-submissions');
      if (response.ok) {
        const data = await response.json();
        setContactSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error loading contact submissions:', error);
    }

  };

  // ========== INVENTORY MANAGEMENT FUNCTIONS ==========
  const loadInventory = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/inventory');
      if (response.ok) {
        const data = await response.json();
        setInventory(data.inventory || []);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const handleInventoryAdjustment = async (productId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/inventory/${productId}/adjust`,
        'POST',
        {
          product_id: productId,
          quantity_change: parseInt(inventoryAdjustment.quantity_change),
          reason: inventoryAdjustment.reason,
          notes: inventoryAdjustment.notes
        }
      );

      if (response.ok) {
        alert('Inventory updated successfully!');
        setShowInventoryForm(false);
        setInventoryAdjustment({ quantity_change: '', reason: '', notes: '' });
        loadInventory();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'Failed to update inventory'));
      }
    } catch (error) {
      console.error('Error adjusting inventory:', error);
      alert('Error adjusting inventory');
    }
  };

  // ========== REVIEWS MANAGEMENT FUNCTIONS ==========
  const loadReviews = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/reviews');
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const moderateReview = async (reviewId, status, notes = '') => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/reviews/${reviewId}`,
        'PATCH',
        { status, admin_notes: notes }
      );

      if (response.ok) {
        alert(`Review ${status} successfully!`);
        loadReviews();
      }
    } catch (error) {
      console.error('Error moderating review:', error);
      alert('Error moderating review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const response = await makeAuthenticatedRequest(`/api/admin/reviews/${reviewId}`, { method: 'DELETE' });
      if (response.ok) {
        alert('Review deleted successfully!');
        loadReviews();
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Error deleting review');
    }
  };

  // ========== CMS EDITOR FUNCTIONS ==========
  const loadCmsPages = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/cms/pages');
      if (response.ok) {
        const data = await response.json();
        setCmsPages(data.pages || []);
      }
    } catch (error) {
      console.error('Error loading CMS pages:', error);
    }
  };

  const handleCmsPageSelect = (page) => {
    setSelectedPage(page);
    setCmsForm({
      title: page.title,
      content: page.content,
      meta_description: page.meta_description || '',
      is_published: page.is_published
    });
    setShowCmsEditor(true);
  };

  const handleCmsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const isNewPage = selectedPage.slug === 'new' || !cmsPages.find(p => p.slug === selectedPage.slug);
      
      const endpoint = isNewPage 
        ? '/api/admin/cms/pages'
        : `/api/admin/cms/pages/${selectedPage.slug}`;
      
      const method = isNewPage ? 'POST' : 'PUT';
      
      const payload = isNewPage 
        ? { ...cmsForm, slug: selectedPage.slug, id: Date.now().toString() }
        : cmsForm;

      const response = await makeAuthenticatedRequest(
        endpoint,
        method,
        payload
      );

      if (response.ok) {
        alert(isNewPage ? 'Page created successfully!' : 'Page updated successfully!');
        setShowCmsEditor(false);
        setSelectedPage(null);
        loadCmsPages();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || `Failed to ${isNewPage ? 'create' : 'update'} page`));
      }
    } catch (error) {
      console.error('Error saving CMS page:', error);
      alert('Error saving page');
    }
  };

  const markContactAsRead = async (submissionId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/contact-submissions/${submissionId}/mark-read`,
        { method: 'PATCH' }
      );
      if (response.ok) {
        // Reload submissions to reflect the change
        loadContactSubmissions();
      }
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };


  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock_quantity: parseInt(productForm.stock_quantity),
        tags: productForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        image_url: productForm.image_urls?.[0] || '', // Use first image as primary for backward compatibility
        image_urls: productForm.image_urls || [] // Store all images
      };

      // Check if we're editing or creating
      const isEditing = editingProduct !== null;
      const url = isEditing ? `/api/products/${editingProduct.id}` : '/api/products';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await makeAuthenticatedRequest(url, {
        method: method,
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        alert(isEditing ? 'Product updated successfully!' : 'Product created successfully!');
        loadProducts();
        resetProductForm();
        setShowProductForm(false);
        setEditingProduct(null);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error submitting product:', error);
      alert('Error submitting product');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      manufacturer: '',
      product_type: 'FDM Printer',
      category: '',
      price: '',
      image_urls: [], // Reset to empty array
      stock_quantity: '',
      sku: '',
      specifications: {
        build_volume: '',
        layer_height: '',
        print_speed: '',
        connectivity: '',
        material_compatibility: '',
        diameter: '',
        color: '',
        weight: ''
      },
      tags: ''
    });
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      confirmed: { color: 'bg-blue-100 text-blue-800', text: 'Confirmed' },
      in_production: { color: 'bg-purple-100 text-purple-800', text: 'In Production' },
      quality_check: { color: 'bg-indigo-100 text-indigo-800', text: 'Quality Check' },
      shipping: { color: 'bg-brand-orange-100 text-brand-orange-800', text: 'Shipping' },
      completed: { color: 'bg-brand-green-100 text-brand-green-800', text: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.color} border-0`}>{config.text}</Badge>;
  };

  const getStockStatusBadge = (quantity) => {
    if (quantity === 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (quantity < 5) {
      return <Badge variant="outline" className="border-brand-orange-500 text-brand-orange-600">Low Stock</Badge>;
    } else {
      return <Badge variant="outline" className="border-green-500 text-green-600">In Stock</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>Please log in to access the admin panel</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-3">
            <Button onClick={navigateHome} variant="outline">
              Return to Home
            </Button>
            {/* Test Admin Access - Development Only */}
            <Button 
              onClick={testLogin}
              className="bg-orange-600 hover:bg-orange-700 w-full"
            >
              🔧 Test Admin Access (Dev Only)
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Checking admin permissions...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You don't have admin permissions for this panel</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="text-sm text-slate-600">
              <p>Current user: {user?.email}</p>
              <p>Admin access is restricted to authorized personnel only.</p>
            </div>
            <Button onClick={navigateHome} variant="outline">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button onClick={navigateHome} variant="ghost" className="flex items-center space-x-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Admin Panel
                </h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-8">
            <TabsTrigger value="products" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Products</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center space-x-2">
              <ShoppingCart className="w-4 h-4" />
              <span>Orders</span>
            </TabsTrigger>
            <TabsTrigger value="marketplace" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Marketplace</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Messages</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Settings</span>
            </TabsTrigger>
            <TabsTrigger value="inventory" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Inventory</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center space-x-2">
              <Star className="w-4 h-4" />
              <span>Reviews</span>
            </TabsTrigger>
            <TabsTrigger value="cms" className="flex items-center space-x-2">
              <Edit className="w-4 h-4" />
              <span>CMS Editor</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Users</span>
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Newsletter</span>
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Files</span>
            </TabsTrigger>
          </TabsList>

          {/* Products Management Tab */}
          <TabsContent value="products">
            <div className="space-y-6">
              {/* Products Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Product Management</h2>
                  <p className="text-slate-600">Manage your 3D printing equipment and materials catalog</p>
                </div>
                <Button 
                  onClick={() => {
                    setShowProductForm(true);
                    // Scroll to top so user can see the form
                    setTimeout(() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 100);
                  }}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </div>

              {/* Product Creation Form */}
              {showProductForm && (
                <Card className="border-green-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle>
                        <CardDescription>
                          {editingProduct ? 'Update product information and images' : 'Create a new product listing for the catalog'}
                        </CardDescription>
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={resetProductForm}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleProductSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Product Name</label>
                          <Input
                            value={productForm.name}
                            onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                            placeholder="e.g., Bambu Lab X1 Carbon"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Manufacturer</label>
                          <Input
                            value={productForm.manufacturer}
                            onChange={(e) => setProductForm({...productForm, manufacturer: e.target.value})}
                            placeholder="e.g., Bambu Lab"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Product Type</label>
                          <Select value={productForm.product_type} onValueChange={(value) => setProductForm({...productForm, product_type: value})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FDM Printer">FDM Printer</SelectItem>
                              <SelectItem value="Resin Printer">Resin Printer</SelectItem>
                              <SelectItem value="Filament">Filament</SelectItem>
                              <SelectItem value="Accessory">Accessory</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Category</label>
                          <Input
                            value={productForm.category}
                            onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                            placeholder="e.g., Professional FDM"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Price ($)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.price}
                            onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                            placeholder="299.99"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Stock Quantity</label>
                          <Input
                            type="number"
                            value={productForm.stock_quantity}
                            onChange={(e) => setProductForm({...productForm, stock_quantity: e.target.value})}
                            placeholder="10"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">SKU</label>
                          <Input
                            value={productForm.sku}
                            onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                            placeholder="BBL-X1C"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Product Images</label>
                          
                          {/* Multiple Images Preview */}
                          {productForm.image_urls && productForm.image_urls.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                              {productForm.image_urls.map((imageUrl, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={imageUrl}
                                    alt={`Product image ${index + 1}`}
                                    className="w-24 h-24 object-cover rounded-lg border border-slate-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="hidden w-24 h-24 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center">
                                    <Package className="w-6 h-6 text-slate-400" />
                                  </div>
                                  
                                  {/* Remove Image Button */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setProductForm(prev => ({
                                        ...prev,
                                        image_urls: prev.image_urls.filter((_, i) => i !== index)
                                      }));
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Upload Options */}
                          <div className="space-y-3">
                            {/* Multiple File Upload */}
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                multiple
                                className="hidden"
                                id="product-images-upload"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => document.getElementById('product-images-upload').click()}
                                className="w-full flex items-center space-x-2"
                              >
                                <Package className="w-4 h-4" />
                                <span>Upload Images (Max 10)</span>
                              </Button>
                            </div>
                            
                            {/* URL Input Alternative */}
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Or add image URL:</label>
                              <div className="flex space-x-2">
                                <Input
                                  placeholder="https://images.unsplash.com/..."
                                  className="text-sm flex-1"
                                  id="manual-image-url"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const urlInput = document.getElementById('manual-image-url');
                                    const url = urlInput.value.trim();
                                    if (url) {
                                      setProductForm(prev => ({
                                        ...prev,
                                        image_urls: [...(prev.image_urls || []), url]
                                      }));
                                      urlInput.value = '';
                                    }
                                  }}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>
                            
                            {/* Image Count Display */}
                            <p className="text-xs text-slate-500">
                              {productForm.image_urls?.length || 0} image(s) added
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <Textarea
                          value={productForm.description}
                          onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                          placeholder="Detailed product description..."
                          rows={3}
                          required
                        />
                      </div>

                      {/* Specifications */}
                      <div>
                        <h4 className="font-medium text-slate-700 mb-3">Specifications</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {productForm.product_type.includes('Printer') && (
                            <>
                              <div>
                                <label className="block text-sm font-medium mb-1">Build Volume</label>
                                <Input
                                  value={productForm.specifications.build_volume}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, build_volume: e.target.value}
                                  })}
                                  placeholder="256 x 256 x 256 mm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Layer Height</label>
                                <Input
                                  value={productForm.specifications.layer_height}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, layer_height: e.target.value}
                                  })}
                                  placeholder="0.05 - 0.3 mm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Print Speed</label>
                                <Input
                                  value={productForm.specifications.print_speed}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, print_speed: e.target.value}
                                  })}
                                  placeholder="500 mm/s"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Connectivity</label>
                                <Input
                                  value={productForm.specifications.connectivity}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, connectivity: e.target.value}
                                  })}
                                  placeholder="Wi-Fi, USB, Ethernet"
                                />
                              </div>
                            </>
                          )}

                          {productForm.product_type === 'Filament' && (
                            <>
                              <div>
                                <label className="block text-sm font-medium mb-1">Material Type</label>
                                <Input
                                  value={productForm.specifications.material_compatibility}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, material_compatibility: e.target.value}
                                  })}
                                  placeholder="PLA, ABS, PETG"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Diameter</label>
                                <Input
                                  value={productForm.specifications.diameter}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, diameter: e.target.value}
                                  })}
                                  placeholder="1.75mm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Available Colors</label>
                                <Input
                                  value={productForm.specifications.color}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, color: e.target.value}
                                  })}
                                  placeholder="Black, White, Red, Blue"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Weight</label>
                                <Input
                                  value={productForm.specifications.weight}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, weight: e.target.value}
                                  })}
                                  placeholder="1kg"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
                        <Input
                          value={productForm.tags}
                          onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                          placeholder="high-speed, enclosed, professional"
                        />
                      </div>

                      <div className="flex space-x-4">
                        <Button type="submit" className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
                          {editingProduct ? 'Save Changes' : 'Create Product'}
                        </Button>
                        <Button type="button" onClick={resetProductForm} variant="outline">
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Products List */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Product Inventory ({products.length})</CardTitle>
                      <CardDescription>Manage your product catalog and inventory levels</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="animate-pulse flex space-x-4">
                          <div className="w-16 h-16 bg-slate-200 rounded"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-slate-200 rounded"></div>
                            <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {products.map(product => (
                        <div key={product.id} className="flex items-center space-x-4 p-4 border border-slate-200 rounded-lg hover:shadow-sm transition-shadow">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-medium text-slate-800">{product.name}</h4>
                                <p className="text-sm text-slate-600">{product.manufacturer} • {product.category}</p>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-lg">{formatPrice(product.price)}</div>
                                <div className="text-sm text-slate-500">SKU: {product.sku}</div>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                {getStockStatusBadge(product.stock_quantity)}
                                <span className="text-sm text-slate-600">Stock: {product.stock_quantity}</span>
                                {product.rating && (
                                  <div className="flex items-center space-x-1">
                                    <span className="text-sm">⭐ {product.rating}</span>
                                    <span className="text-sm text-slate-500">({product.review_count})</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to product detail page
                                    window.open(`/products/${product.id}`, '_blank');
                                  }}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    try {
                                      // Set editing product and prepare form data
                                      setEditingProduct(product);
                                      
                                      // Properly merge all available images
                                      let allImages = [];
                                      
                                      // Add single image_url if exists
                                      if (product.image_url) {
                                        allImages.push(product.image_url);
                                      }
                                      
                                      // Add images array if exists
                                      if (product.images && Array.isArray(product.images)) {
                                        allImages = allImages.concat(product.images);
                                      }
                                      
                                      // Add image_urls array if exists  
                                      if (product.image_urls && Array.isArray(product.image_urls)) {
                                        allImages = allImages.concat(product.image_urls);
                                      }
                                      
                                      // Remove duplicates
                                      allImages = [...new Set(allImages)];
                                      
                                      const formData = {
                                        name: product.name,
                                        description: product.description,
                                        manufacturer: product.manufacturer,
                                        product_type: product.product_type,
                                        category: product.category,
                                        price: product.price?.toString() || '',
                                        image_urls: allImages,
                                        stock_quantity: product.stock_quantity?.toString() || '',
                                        sku: product.sku || '',
                                        specifications: product.specifications || {
                                          build_volume: '',
                                          layer_height: '',
                                          print_speed: '',
                                          connectivity: '',
                                          material_compatibility: '',
                                          diameter: '',
                                          color: '',
                                          weight: ''
                                        },
                                        tags: (product.tags || []).join(', ')
                                      };
                                      
                                      setProductForm(formData);
                                      setShowProductForm(true);
                                      
                                      // UX FIX: Scroll to top so user can see the form
                                      setTimeout(() => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }, 100);
                                      
                                    } catch (error) {
                                      console.error('ERROR in Edit button click handler:', error);
                                      alert(`Error opening edit form: ${error.message}`);
                                    }
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete ${product.name}?`)) {
                                      // TODO: Implement delete functionality
                                      alert('Delete functionality will be implemented in next phase');
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Management Tab */}
          <TabsContent value="orders">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Order Management</h2>
                <p className="text-slate-600">Track and manage customer orders</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Orders ({orders.length})</CardTitle>
                  <CardDescription>Customer orders from both services and e-commerce</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {orders.map(order => (
                      <div key={order.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">Order #{order.id.slice(0, 8)}</div>
                            <div className="text-sm text-slate-600">{order.filename}</div>
                            <div className="text-sm text-slate-500">{formatDate(order.created_at)}</div>
                            {order.tracking_number && (
                              <div className="text-sm text-blue-600 mt-1">
                                📦 Tracking: {order.tracking_number}
                              </div>
                            )}
                          </div>
                          <div className="text-right space-y-2">
                            <div className="font-semibold text-lg">{formatPrice(order.estimated_price)}</div>
                            {getOrderStatusBadge(order.status)}
                            <Button 
                              size="sm" 
                              onClick={() => openOrderUpdate(order)}
                              className="w-full"
                            >
                              Update Order
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Material:</span> {order.material}
                          </div>
                          <div>
                            <span className="font-medium">Quality:</span> {order.quality}
                          </div>
                          <div>
                            <span className="font-medium">Delivery:</span> {order.delivery_weeks} weeks
                          </div>
                        </div>

                        {order.customer_notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <span className="font-medium text-sm">Notes:</span> {order.customer_notes}
                          </div>
                        )}
                        
                        {order.latest_note && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                            <span className="font-medium">Latest Update:</span> {order.latest_note}
                          </div>
                        )}
                      </div>
                    ))}

                    {orders.length === 0 && (
                      <div className="text-center py-8">
                        <ShoppingCart className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-600">No orders found</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Order Update Modal */}
              {showOrderUpdateModal && selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold">Update Order #{selectedOrder.id.slice(0, 8)}</h3>
                        <Button variant="outline" size="sm" onClick={() => setShowOrderUpdateModal(false)}>
                          Close
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Status Update */}
                        <div>
                          <label className="block text-sm font-medium mb-2">Order Status</label>
                          <Select 
                            value={orderUpdateForm.status} 
                            onValueChange={(value) => setOrderUpdateForm({...orderUpdateForm, status: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="in_production">In Production</SelectItem>
                              <SelectItem value="quality_check">Quality Check</SelectItem>
                              <SelectItem value="shipping">Shipping</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Tracking Information */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Tracking Number</label>
                            <Input
                              value={orderUpdateForm.tracking_number}
                              onChange={(e) => setOrderUpdateForm({...orderUpdateForm, tracking_number: e.target.value})}
                              placeholder="Enter tracking number"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Carrier</label>
                            <Input
                              value={orderUpdateForm.tracking_carrier}
                              onChange={(e) => setOrderUpdateForm({...orderUpdateForm, tracking_carrier: e.target.value})}
                              placeholder="e.g., USPS, UPS, FedEx"
                            />
                          </div>
                        </div>
                        
                        {/* Customer Note */}
                        <div>
                          <label className="block text-sm font-medium mb-2">Customer Note (Visible to Customer)</label>
                          <Textarea
                            value={orderUpdateForm.note}
                            onChange={(e) => setOrderUpdateForm({...orderUpdateForm, note: e.target.value})}
                            placeholder="Add a note for the customer..."
                            rows={3}
                          />
                        </div>
                        
                        {/* Internal Note */}
                        <div>
                          <label className="block text-sm font-medium mb-2">Internal Note (Staff Only)</label>
                          <Textarea
                            value={orderUpdateForm.internal_note}
                            onChange={(e) => setOrderUpdateForm({...orderUpdateForm, internal_note: e.target.value})}
                            placeholder="Add internal notes..."
                            rows={2}
                          />
                        </div>
                        
                        {/* Notify Customer */}
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={orderUpdateForm.notify_customer}
                            onChange={(e) => setOrderUpdateForm({...orderUpdateForm, notify_customer: e.target.checked})}
                            id="notify-customer"
                          />
                          <label htmlFor="notify-customer" className="text-sm">
                            Send email notification to customer
                          </label>
                        </div>
                        
                        {/* Submit Button */}
                        <div className="flex space-x-2">
                          <Button onClick={submitOrderUpdate} className="flex-1">
                            Update Order
                          </Button>
                          <Button variant="outline" onClick={() => setShowOrderUpdateModal(false)}>
                            Cancel
                          </Button>
                        </div>
                        
                        {/* Order History */}
                        {orderHistory.length > 0 && (
                          <div className="mt-6 pt-6 border-t">
                            <h4 className="font-semibold mb-3">Update History</h4>
                            <div className="space-y-2">
                              {orderHistory.map((entry, index) => (
                                <div key={index} className="text-sm p-3 bg-slate-50 rounded">
                                  <div className="font-medium">{new Date(entry.timestamp).toLocaleString()}</div>
                                  <div className="text-slate-600">By: {entry.updated_by}</div>
                                  {entry.changes && Object.keys(entry.changes).length > 0 && (
                                    <div className="mt-1 text-slate-700">
                                      Changes: {JSON.stringify(entry.changes)}
                                    </div>
                                  )}
                                  {entry.customer_note && (
                                    <div className="mt-1 text-blue-700">Note: {entry.customer_note}</div>
                                  )}
                                  {entry.internal_note && (
                                    <div className="mt-1 text-orange-700">Internal: {entry.internal_note}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Marketplace Management Tab */}
          <TabsContent value="marketplace">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Marketplace Management</h2>
                    <p className="text-slate-600">Manage designer applications and marketplace listings</p>
                  </div>
                  <Button 
                    onClick={loadMarketplaceStats}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Refresh Stats</span>
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Pending Applications</p>
                        <p className="text-2xl font-bold text-slate-800">{marketplaceStats.applications}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Active Listings</p>
                        <p className="text-2xl font-bold text-slate-800">{marketplaceStats.activeListings}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Sales</p>
                        <p className="text-2xl font-bold text-slate-800">${marketplaceStats.totalSales.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Marketplace Controls</CardTitle>
                  <CardDescription>
                    Quick access to marketplace management features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Button 
                      onClick={() => {
                        setMarketplaceView('overview');
                      }}
                      variant={marketplaceView === 'overview' ? 'default' : 'outline'}
                      className="flex items-center space-x-2"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span>Overview</span>
                    </Button>
                    
                    <Button 
                      onClick={() => {
                        setMarketplaceView('listings');
                        loadAllListings();
                      }}
                      variant={marketplaceView === 'listings' ? 'default' : 'outline'}
                      className="flex items-center space-x-2"
                    >
                      <Package className="w-4 h-4" />
                      <span>All Listings</span>
                    </Button>

                    <Button 
                      onClick={() => {
                        setMarketplaceView('designers');
                        loadAllDesigners();
                      }}
                      variant={marketplaceView === 'designers' ? 'default' : 'outline'}
                      className="flex items-center space-x-2"
                    >
                      <Users className="w-4 h-4" />
                      <span>All Designers</span>
                    </Button>
                    
                    <Button 
                      onClick={() => window.open('/marketplace', '_blank')}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Public</span>
                    </Button>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Revenue Sharing</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Downloads:</span>
                        <span className="font-semibold ml-2">30% Platform / 70% Designer</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Print Orders:</span>
                        <span className="font-semibold ml-2">80% Platform / 20% Designer</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pending Listings Management */}
              <Card>
                <CardHeader>
                  <CardTitle>Draft Listings (Pending Approval)</CardTitle>
                  <CardDescription>
                    Review and approve new marketplace submissions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingListings.length > 0 ? (
                    <div className="space-y-4">
                      {pendingListings.map((listing) => (
                        <div key={listing.id} className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold text-slate-800">{listing.title}</h4>
                              <p className="text-sm text-slate-600">by {listing.designer_name}</p>
                              <p className="text-sm text-slate-500 mt-1">{listing.description}</p>
                              <div className="flex items-center space-x-4 mt-2 text-sm">
                                <span className="text-green-600">Personal: ${listing.personal_price}</span>
                                <span className="text-blue-600">Commercial: ${listing.commercial_price}</span>
                                <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                                  {listing.category}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveListing(listing.id)}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleRejectListing(listing.id)}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No draft listings at this time</p>
                      <p className="text-sm text-slate-500 mt-2">New designer submissions will appear here for review</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Designer Applications */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Designer Applications</CardTitle>
                  <CardDescription>
                    Latest designer applications (all statuses)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {recentApplications.length > 0 ? (
                    <div className="space-y-4">
                      {recentApplications.map((application) => (
                        <div key={application.id} className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-semibold text-slate-800">{application.designer_name}</h4>
                                <Badge variant="outline" className={`text-xs ${
                                  application.status === 'approved' ? 'text-green-700 border-green-300' :
                                  application.status === 'pending' ? 'text-yellow-700 border-yellow-300' :
                                  'text-red-700 border-red-300'
                                }`}>
                                  {application.status}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">{application.user_email}</p>
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{application.bio}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                Applied: {new Date(application.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveApplication(application.id)}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleRejectApplication(application.id)}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No recent applications</p>
                      <p className="text-sm text-slate-500 mt-2">Designer applications will appear here for admin review</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* ALL LISTINGS VIEW */}
              {marketplaceView === 'listings' && (
                <Card>
                  <CardHeader>
                    <CardTitle>All Marketplace Listings</CardTitle>
                    <CardDescription>View and manage all CAD listings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {allListings.map((listing) => (
                        <div key={listing.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{listing.title}</h4>
                              <p className="text-sm text-slate-600 mt-1">{listing.description?.substring(0, 100)}...</p>
                              <div className="flex items-center space-x-3 mt-2 text-xs text-slate-500">
                                <span>Designer: {listing.designer_display_name || listing.designer_id?.substring(0, 8)}</span>
                                <span>•</span>
                                <span>Personal: ${listing.personal_price}</span>
                                <span>•</span>
                                <span>Commercial: ${listing.commercial_price}</span>
                                <Badge variant={listing.status === 'active' ? 'default' : 'outline'}>
                                  {listing.status}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <Button size="sm" variant="outline" onClick={() => window.open(`/marketplace/listing/${listing.id}`, '_blank')}>
                                <Eye className="w-3 h-3 mr-1" /> View
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteListing(listing.id)}>
                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {allListings.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No listings found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ALL DESIGNERS VIEW */}
              {marketplaceView === 'designers' && (
                <Card>
                  <CardHeader>
                    <CardTitle>All Designers</CardTitle>
                    <CardDescription>Manage designer accounts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {allDesigners.map((designer) => (
                        <div key={designer.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{designer.name}</h4>
                              <p className="text-sm text-slate-600">{designer.email}</p>
                              <div className="flex items-center space-x-2 mt-2">
                                {designer.is_suspended && (
                                  <Badge variant="destructive">Suspended</Badge>
                                )}
                                {designer.designer_display_name && (
                                  <Badge variant="outline">Display: {designer.designer_display_name}</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              {designer.is_suspended ? (
                                <Button size="sm" onClick={() => unsuspendDesigner(designer.id)}>
                                  Unsuspend
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => suspendDesigner(designer.id)}>
                                  Suspend
                                </Button>
                              )}
                              <Button size="sm" variant="destructive" onClick={() => removeDesignerStatus(designer.id)}>
                                Remove Status
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {allDesigners.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No designers found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>



          {/* Contact Messages Tab */}
          <TabsContent value="contact">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Contact Form Submissions</h2>
                <p className="text-slate-600">Messages from customers via the contact form</p>
              </div>

              {contactSubmissions.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No contact form submissions yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {contactSubmissions.map((submission) => (
                    <Card key={submission.id} className={submission.read ? 'opacity-75' : 'border-blue-200'}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg flex items-center gap-2">
                              {!submission.read && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  New
                                </span>
                              )}
                              {submission.subject}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              From: <strong>{submission.name}</strong> ({submission.email})
                              <br />
                              Date: {new Date(submission.submitted_at).toLocaleString()}
                            </CardDescription>
                          </div>
                          {!submission.read && (
                            <Button
                              onClick={() => markContactAsRead(submission.id)}
                              variant="outline"
                              size="sm"
                            >
                              Mark as Read
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-slate-700 whitespace-pre-wrap">{submission.message}</p>
                        </div>
                        <div className="mt-4">
                          <a
                            href={`mailto:${submission.email}?subject=Re: ${submission.subject}`}
                            className="inline-flex items-center text-blue-600 hover:text-blue-700"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Reply via Email
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Analytics Dashboard</h2>
                <p className="text-slate-600">Business insights and performance metrics</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Products</p>
                        <p className="text-2xl font-bold text-slate-800">{products.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <ShoppingCart className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Orders</p>
                        <p className="text-2xl font-bold text-slate-800">{orders.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Revenue</p>
                        <p className="text-2xl font-bold text-slate-800">
                          {formatPrice(orders.reduce((total, order) => total + (order.final_price || order.estimated_price), 0))}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Low Stock Items</p>
                        <p className="text-2xl font-bold text-slate-800">
                          {products.filter(p => p.stock_quantity < 5).length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Low Stock Alert */}
              {products.filter(p => p.stock_quantity < 5).length > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="text-orange-800">⚠️ Low Stock Alert</CardTitle>
                    <CardDescription>The following products need restocking</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {products
                        .filter(p => p.stock_quantity < 5)
                        .map(product => (
                          <div key={product.id} className="flex justify-between items-center">
                            <span className="font-medium">{product.name}</span>
                            <Badge variant="outline" className="border-orange-500 text-orange-600">
                              {product.stock_quantity} left
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Admin Settings</h2>
                <p className="text-slate-600">System configuration and management tools</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>Manage user accounts and permissions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span>Total Users</span>
                        <Badge variant="outline">Coming Soon</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Admin Users</span>
                        <Badge variant="outline">1 (You)</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>System Health</CardTitle>
                    <CardDescription>Monitor system status and performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span>Database Connection</span>
                        <Badge variant="outline" className="border-green-500 text-green-600">Online</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Payment System</span>
                        <Badge variant="outline" className="border-green-500 text-green-600">Active</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>3D File Processing</span>
                        <Badge variant="outline" className="border-green-500 text-green-600">Operational</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>


          {/* Inventory Management Tab */}
          <TabsContent value="inventory">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Inventory Management</h2>
                  <p className="text-slate-600">Manage product stock levels</p>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" onClick={async () => {
                    try {
                      const response = await makeAuthenticatedRequest('/api/admin/inventory/export');
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'inventory_export.csv';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }
                    } catch (error) {
                      alert('Error exporting inventory');
                    }
                  }}>
                    Export CSV
                  </Button>
                  <Button onClick={() => document.getElementById('inventory-csv-upload').click()}>
                    Bulk Upload CSV
                  </Button>
                  <input
                    id="inventory-csv-upload"
                    type="file"
                    accept=".csv"
                    style={{display: 'none'}}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const idToken = await getIdToken();
                        const response = await fetch(
                          `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/inventory/bulk-upload`,
                          {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${idToken}`
                            },
                            body: formData
                          }
                        );
                        
                        if (response.ok) {
                          const data = await response.json();
                          alert(`Upload successful! ${data.message}\nErrors: ${data.results.errors.length}`);
                          loadInventory();
                        } else {
                          alert('Error uploading CSV');
                        }
                      } catch (error) {
                        alert('Error uploading CSV');
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-800">
                    <strong>CSV Format:</strong> product_id, sku, stock_quantity, low_stock_threshold
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Use product_id OR sku to identify products. Download current inventory to see format.
                  </p>
                </CardContent>
              </Card>

              {showInventoryForm && selectedProductForInventory && (
                <Card>
                  <CardHeader>
                    <CardTitle>Adjust Inventory - {selectedProductForInventory.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Quantity Change</label>
                        <Input
                          type="number"
                          placeholder="Enter positive for restock, negative for removal"
                          value={inventoryAdjustment.quantity_change}
                          onChange={(e) => setInventoryAdjustment({...inventoryAdjustment, quantity_change: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Reason</label>
                        <Input
                          placeholder="e.g., New shipment, Damage, Sale"
                          value={inventoryAdjustment.reason}
                          onChange={(e) => setInventoryAdjustment({...inventoryAdjustment, reason: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                        <Textarea
                          placeholder="Additional details..."
                          value={inventoryAdjustment.notes}
                          onChange={(e) => setInventoryAdjustment({...inventoryAdjustment, notes: e.target.value})}
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button onClick={() => handleInventoryAdjustment(selectedProductForInventory.id)}>
                          Save Adjustment
                        </Button>
                        <Button variant="outline" onClick={() => {
                          setShowInventoryForm(false);
                          setSelectedProductForInventory(null);
                          setInventoryAdjustment({ quantity_change: '', reason: '', notes: '' });
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Products Inventory</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {products.map((product) => {
                      const inventoryItem = inventory.find(i => i.product_id === product.id);
                      const stockQty = inventoryItem?.stock_quantity || 0;
                      const status = inventoryItem?.status || 'in_stock';
                      
                      return (
                        <div key={product.id} className="flex items-center justify-between border-b pb-4">
                          <div className="flex-1">
                            <h4 className="font-medium">{product.name}</h4>
                            <p className="text-sm text-slate-600">SKU: {product.sku || 'N/A'}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-center">
                              <p className="text-sm text-slate-600">Stock</p>
                              <p className="text-lg font-bold">{stockQty}</p>
                            </div>
                            <Badge variant={status === 'in_stock' ? 'default' : status === 'low_stock' ? 'warning' : 'destructive'}>
                              {status === 'in_stock' ? 'In Stock' : status === 'low_stock' ? 'Low Stock' : 'Out of Stock'}
                            </Badge>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedProductForInventory(product);
                                setShowInventoryForm(true);
                              }}
                            >
                              Adjust
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reviews Management Tab */}
          <TabsContent value="reviews">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Customer Reviews</h2>
                  <p className="text-slate-600">Moderate product and listing reviews</p>
                </div>
                <Select value={reviewFilter} onValueChange={setReviewFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter reviews" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reviews</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {reviews
                      .filter(r => reviewFilter === 'all' || r.status === reviewFilter)
                      .map((review) => (
                        <div key={review.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="font-medium">{review.user_name}</span>
                                {review.verified_purchase && (
                                  <Badge variant="outline" className="text-green-600 border-green-600">
                                    Verified Purchase
                                  </Badge>
                                )}
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                    />
                                  ))}
                                </div>
                              </div>
                              <h4 className="font-semibold text-slate-900">{review.title}</h4>
                              <p className="text-slate-600 mt-1">{review.comment}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                {new Date(review.created_at).toLocaleDateString()} • Helpful: {review.helpful_count}
                              </p>
                              {review.admin_notes && (
                                <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                                  <strong>Admin Notes:</strong> {review.admin_notes}
                                </div>
                              )}
                            </div>
                            <Badge
                              variant={review.status === 'approved' ? 'default' : review.status === 'pending' ? 'warning' : 'destructive'}
                            >
                              {review.status}
                            </Badge>
                          </div>
                          <div className="flex space-x-2">
                            {review.status !== 'approved' && (
                              <Button size="sm" onClick={() => moderateReview(review.id, 'approved')}>
                                Approve
                              </Button>
                            )}
                            {review.status !== 'rejected' && (
                              <Button size="sm" variant="outline" onClick={() => {
                                const notes = prompt('Rejection reason (optional):');
                                moderateReview(review.id, 'rejected', notes || '');
                              }}>
                                Reject
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => deleteReview(review.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    {reviews.filter(r => reviewFilter === 'all' || r.status === reviewFilter).length === 0 && (
                      <p className="text-center text-slate-500 py-8">No reviews found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CMS Editor Tab */}
          <TabsContent value="cms">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">CMS Editor</h2>
                  <p className="text-slate-600">Edit website content pages</p>
                </div>
                {!showCmsEditor && (
                  <Button onClick={() => {
                    setSelectedPage({ slug: 'new', title: '', content: '', meta_description: '', is_published: true });
                    setCmsForm({ title: '', content: '', meta_description: '', is_published: true });
                    setShowCmsEditor(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Page
                  </Button>
                )}
              </div>

              {showCmsEditor && selectedPage ? (
                <Card>
                  <CardHeader>
                    <CardTitle>{selectedPage.slug === 'new' ? 'Create New Page' : `Edit Page: ${selectedPage.slug}`}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCmsSubmit} className="space-y-4">
                      {selectedPage.slug === 'new' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Page Slug (URL path)</label>
                          <Input
                            placeholder="e.g., privacy-policy"
                            onChange={(e) => setSelectedPage({...selectedPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                            required
                          />
                          <p className="text-xs text-slate-500 mt-1">Will appear as: /pages/{selectedPage.slug}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium mb-2">Page Title</label>
                        <Input
                          value={cmsForm.title}
                          onChange={(e) => setCmsForm({...cmsForm, title: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Content (HTML)</label>
                        <Textarea
                          value={cmsForm.content}
                          onChange={(e) => setCmsForm({...cmsForm, content: e.target.value})}
                          rows={15}
                          className="font-mono text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Meta Description</label>
                        <Textarea
                          value={cmsForm.meta_description}
                          onChange={(e) => setCmsForm({...cmsForm, meta_description: e.target.value})}
                          rows={2}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={cmsForm.is_published}
                          onChange={(e) => setCmsForm({...cmsForm, is_published: e.target.checked})}
                          id="is_published"
                        />
                        <label htmlFor="is_published" className="text-sm font-medium">
                          Published (visible to public)
                        </label>
                      </div>
                      <div className="flex space-x-2">
                        <Button type="submit">Save Changes</Button>
                        <Button type="button" variant="outline" onClick={() => {
                          setShowCmsEditor(false);
                          setSelectedPage(null);
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Website Pages</CardTitle>
                    <CardDescription>Click on a page to edit its content</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {cmsPages.map((page) => (
                        <div
                          key={page.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                          onClick={() => handleCmsPageSelect(page)}
                        >
                          <div>
                            <h4 className="font-medium">{page.title}</h4>
                            <p className="text-sm text-slate-600">/{page.slug}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant={page.is_published ? 'default' : 'outline'}>
                              {page.is_published ? 'Published' : 'Draft'}
                            </Badge>
                            <Edit className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                      {cmsPages.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No pages found. Create default pages to get started.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>


          {/* User Management Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">User Management</h2>
                  <p className="text-slate-600">Manage user accounts and permissions</p>
                </div>
                <Button onClick={loadAllUsers}>
                  Refresh Users
                </Button>
              </div>

              <div className="mb-4">
                <Input
                  placeholder="Search users by name or email..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="max-w-md"
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>View and manage platform users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allUsers
                      .filter(u => 
                        !userSearchTerm || 
                        u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
                      )
                      .map((user) => (
                        <div key={user.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{user.name || 'Unnamed'}</h4>
                              <p className="text-sm text-slate-600">{user.email}</p>
                              <div className="flex items-center space-x-2 mt-2 text-xs">
                                <Badge variant="outline">
                                  {user.role_info?.role || 'Customer'}
                                </Badge>
                                {user.is_designer && <Badge>Designer</Badge>}
                                {user.is_banned && <Badge variant="destructive">Banned</Badge>}
                                {user.is_suspended && <Badge variant="destructive">Suspended</Badge>}
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              {user.is_banned ? (
                                <Button size="sm" onClick={() => unbanUser(user.id)}>
                                  Unban
                                </Button>
                              ) : (
                                <Button size="sm" variant="destructive" onClick={() => banUser(user.id)}>
                                  Ban User
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    {allUsers.filter(u => 
                      !userSearchTerm || 
                      u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <p className="text-center text-slate-500 py-8">No users found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* File Downloads Tab */}
          <TabsContent value="files">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Service Files & Downloads</h2>
                <p className="text-slate-600">Download CAD files from quotes and service requests</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Button onClick={loadQuotes} variant="outline">
                  Load Quote Requests
                </Button>
                <Button onClick={loadServiceRequests} variant="outline">
                  Load Service Requests
                </Button>
              </div>

              {/* Quote Files */}
              {quotes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Quote Request Files ({quotes.length})</CardTitle>
                    <CardDescription>CAD files from instant quote requests</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {quotes.map((quote) => (
                        <div key={quote.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium">{quote.filename}</h4>
                              <p className="text-sm text-slate-600 mt-1">
                                Email: {quote.contact_email}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                Material: {quote.material} | Quality: {quote.quality} | Price: ${quote.estimated_price}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(quote.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex flex-col space-y-1">
                              {quote.file_available ? (
                                <Button size="sm" onClick={() => downloadQuoteFile(quote.id)}>
                                  <Package className="w-3 h-3 mr-1" /> Download
                                </Button>
                              ) : (
                                <Badge variant="outline">No File</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Service Request Files */}
              {serviceRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Service Request Files ({serviceRequests.length})</CardTitle>
                    <CardDescription>Files from service inquiries</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {serviceRequests.map((request) => (
                        <div key={request.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium">{request.service_type}</h4>
                              <p className="text-sm text-slate-600 mt-1">
                                From: {request.contact_name} ({request.contact_email})
                              </p>
                              {request.filename && (
                                <p className="text-xs text-slate-500 mt-1">
                                  File: {request.filename}
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(request.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex flex-col space-y-1">
                              {request.file_available && request.files_attached ? (
                                <Button size="sm" onClick={() => downloadServiceFile(request.id)}>
                                  <Package className="w-3 h-3 mr-1" /> Download
                                </Button>
                              ) : (
                                <Badge variant="outline">No File</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {quotes.length === 0 && serviceRequests.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">Click the buttons above to load files</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Newsletter Subscribers</CardTitle>
                <CardDescription>Manage email subscribers for your newsletter</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Search by email..."
                    value={newsletterSearchTerm}
                    onChange={(e) => setNewsletterSearchTerm(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                
                <div className="rounded-md border">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-3 text-left font-medium">Email</th>
                        <th className="p-3 text-left font-medium">Name</th>
                        <th className="p-3 text-left font-medium">Location</th>
                        <th className="p-3 text-left font-medium">Subscribed Date</th>
                        <th className="p-3 text-left font-medium">Status</th>
                        <th className="p-3 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterSubscribers
                        .filter(sub => 
                          sub.email.toLowerCase().includes(newsletterSearchTerm.toLowerCase())
                        )
                        .map((subscriber) => (
                          <tr key={subscriber.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <div className="font-medium">{subscriber.email}</div>
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {subscriber.first_name || subscriber.last_name ? (
                                <span>{subscriber.first_name} {subscriber.last_name}</span>
                              ) : (
                                <span className="text-slate-400 italic">Not provided</span>
                              )}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {subscriber.city || subscriber.state_province || subscriber.country ? (
                                <div>
                                  {subscriber.city && <div>{subscriber.city}</div>}
                                  {subscriber.state_province && <div>{subscriber.state_province}</div>}
                                  {subscriber.country && <div>{subscriber.country}</div>}
                                </div>
                              ) : (
                                <span className="text-slate-400 italic">Not provided</span>
                              )}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {new Date(subscriber.subscribed_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              {subscriber.profile_completed ? (
                                <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Complete</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-600">Email Only</Badge>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <Button
                                onClick={() => removeNewsletterSubscriber(subscriber.email)}
                                variant="destructive"
                                size="sm"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  
                  {newsletterSubscribers.filter(sub => 
                    sub.email.toLowerCase().includes(newsletterSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div className="py-8 text-center text-slate-500">
                      No subscribers found
                    </div>
                  )}
                </div>
                
                <div className="mt-4 text-sm text-slate-600">
                  Total subscribers: {newsletterSubscribers.length}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;